import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const FILAS = [
  ['servicios_resultado_corregir', 'Servicios con resultado mal escrito (ej. "prenada" sin ñ): no contaban en la tasa de preñez'],
  ['servicios_duplicados', 'Servicios duplicados (misma hembra, fecha y toro)'],
  ['pesajes_duplicados', 'Pesajes duplicados (mismo animal, fecha y peso)'],
  ['pariciones_duplicadas', 'Pariciones duplicadas (misma madre y fecha)'],
  ['pesajes_huerfanos', 'Pesajes de animales que ya no existen'],
];

export const LimpiezaDatos = () => {
  const { user } = useAuth();
  const [reporte, setReporte] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const correr = async (ejecutar) => {
    if (ejecutar && !window.confirm('Se eliminaran los registros duplicados (se conserva el mas antiguo) y se corregiran los resultados. Esta accion no se puede deshacer. ¿Continuar?')) return;
    setLoading(true); setMsg('');
    try {
      const res = await api.post(`/mantenimiento/limpiar/${user.establecimiento_id}?ejecutar=${ejecutar}`);
      setReporte(res.data);
      if (ejecutar) setMsg('Limpieza aplicada. Los indicadores del Dashboard ya reflejan los datos corregidos.');
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error al analizar los datos');
    } finally { setLoading(false); }
  };

  const total = reporte ? FILAS.reduce((s, [k]) => s + (reporte[k] || 0), 0) : 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6" data-testid="limpieza-datos">
      <h3 className="font-semibold text-gray-800">Limpieza de datos duplicados</h3>
      <p className="text-sm text-gray-500 mt-1">
        Si importaste el mismo archivo mas de una vez, aca podes detectar y eliminar los registros repetidos. Nunca se borran animales.
      </p>
      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={() => correr(false)} disabled={loading} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50" data-testid="limpieza-analizar-btn">
          {loading ? 'Analizando...' : 'Analizar'}
        </button>
        {reporte && !reporte.ejecutado && total > 0 && (
          <button onClick={() => correr(true)} disabled={loading} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50" data-testid="limpieza-corregir-btn">
            Corregir {total} registros
          </button>
        )}
      </div>
      {reporte && (
        <div className="mt-4 text-sm" data-testid="limpieza-reporte">
          {FILAS.map(([k, label]) => (
            <div key={k} className="flex justify-between py-1.5 border-b border-gray-100">
              <span className="text-gray-600">{label}</span>
              <span className={`font-semibold ${reporte[k] > 0 && !reporte.ejecutado ? 'text-red-600' : 'text-gray-800'}`} data-testid={`limpieza-${k}`}>{reporte[k]}</span>
            </div>
          ))}
          {reporte.animales_sin_sexo > 0 && (
            <p className="text-xs text-amber-700 mt-3">
              {reporte.animales_sin_sexo} animales no tienen sexo cargado (creados desde la balanza). Completalos desde la ficha de cada animal para que cuenten en la distribucion del Dashboard.
            </p>
          )}
          {total === 0 && !reporte.ejecutado && <p className="text-green-700 text-xs mt-3">No se encontraron duplicados.</p>}
        </div>
      )}
      {msg && <p className="text-sm mt-3 text-emerald-700" data-testid="limpieza-msg">{msg}</p>}

      <VincularPadres establecimientoId={user.establecimiento_id} />
    </div>
  );
};

const VincularPadres = ({ establecimientoId }) => {
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const correr = async (ejecutar) => {
    if (ejecutar && !window.confirm('Se asignara el padre a los terneros sin padre segun el servicio registrado de la madre. ¿Continuar?')) return;
    setLoading(true); setMsg('');
    try {
      const res = await api.post(`/mantenimiento/vincular-padres/${establecimientoId}?ejecutar=${ejecutar}`);
      setRep(res.data);
      if (ejecutar) setMsg(`Listo: ${res.data.padre_toro_propio + res.data.padre_externo} terneros vinculados a su padre.`);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error al vincular padres');
    } finally { setLoading(false); }
  };

  const vinculables = rep ? rep.padre_toro_propio + rep.padre_externo + rep.madre_completada : 0;

  return (
    <div className="mt-6 pt-5 border-t border-gray-200" data-testid="vincular-padres">
      <h3 className="font-semibold text-gray-800">Vincular padres de terneros</h3>
      <p className="text-sm text-gray-500 mt-1">
        Asigna el padre a los terneros que tienen madre pero no padre, usando el servicio (monta o IATF) registrado de la madre unos 250-320 dias antes del parto. Si el toro no es un animal tuyo (semen), queda como padre externo.
      </p>
      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={() => correr(false)} disabled={loading} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50" data-testid="padres-analizar-btn">
          {loading ? 'Analizando...' : 'Analizar'}
        </button>
        {rep && !rep.ejecutado && vinculables > 0 && (
          <button onClick={() => correr(true)} disabled={loading} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50" data-testid="padres-vincular-btn">
            Vincular {vinculables} registros
          </button>
        )}
      </div>
      {rep && (
        <div className="mt-4 text-sm" data-testid="padres-reporte">
          {[
            ['candidatos', 'Terneros con madre y sin padre'],
            ['padre_toro_propio', 'Padre identificado: toro propio'],
            ['padre_externo', 'Padre identificado: externo / semen (IATF)'],
            ['sin_servicio', 'Sin servicio de la madre registrado (no se puede deducir)'],
            ['madre_completada', 'Terneros a los que se les completa la madre desde la paricion'],
          ].map(([k, label]) => (
            <div key={k} className="flex justify-between py-1.5 border-b border-gray-100">
              <span className="text-gray-600">{label}</span>
              <span className="font-semibold text-gray-800" data-testid={`padres-${k}`}>{rep[k]}</span>
            </div>
          ))}
        </div>
      )}
      {msg && <p className="text-sm mt-3 text-emerald-700" data-testid="padres-msg">{msg}</p>}
    </div>
  );
};
